import { get, put } from "@/services/axiosUitils";
import PreferencesService from "./PreferencesService";

export interface ProfileApiResponse {
  success: {
    code: string;
    message: string;
  };
  gender: string;
  height_cm: number | null;
  height_inches: string;
  height_ft: number | null;
  weight_kg: number;
  weight_lbs: number | null;
  target_weight_kg: number;
  target_weight_lbs: number | null;
  photo: string | null;
  name: string;
  email: string;
  age: number;
  date_of_birth: string | null; // DD/MM/YYYY format
  blood_type: string | null;
  race: string | null;
  goals: {
    weight_loss_goal: string;
    activity_level: string;
    diet_type: string;
    fasting_window: string;
  };
}

export interface ProcessedProfileData {
  // Basic Profile
  name: string;
  email: string;
  age: number;
  gender: string;
  photo: string | null;
  date_of_birth: string | null; // DD/MM/YYYY format
  blood_type: string | null;
  race: string | null;

  // Physical Information (with user preference units)
  weight: number; // Display value based on user preference
  weightUnit: string; // kg or lbs
  weightKg: number; // original kg value
  weightLbs: number; // calculated lbs value

  height: string; // Display value based on user preference
  heightUnit: string; // cm or ft_in
  heightInches: string; // original inches format
  heightCm: number; // calculated/original cm value
  height_ft: number | null; // feet value
  height_inches: number | null; // inches value

  // Target Information
  targetWeight: number; // Display value based on user preference
  targetWeightKg: number; // original kg value
  targetWeightLbs: number; // calculated lbs value

  // Goals & Preferences (formatted)
  weightLossGoal: string;
  activityLevel: string;
  dietType: string;
  fastingWindow: string;
  fastingPreference: string;
}

class ProfileService {
  private static instance: ProfileService;

  private constructor() {}

  public static getInstance(): ProfileService {
    if (!ProfileService.instance) {
      ProfileService.instance = new ProfileService();
    }
    return ProfileService.instance;
  }

  // Convert kg to lbs
  private kgToLbs(kg: number): number {
    return Math.round(kg * 2.20462 * 10) / 10; // Round to 1 decimal
  }

  // Convert cm to feet'inches format
  private cmToFeetInches(cm: number): string {
    const totalInches = cm / 2.54;
    const feet = Math.floor(totalInches / 12);
    const inches = Math.round(totalInches % 12);
    return `${feet}'${inches}`;
  }

  // Convert height from feet'inches format to cm
  private parseHeight(heightInches: string): number {
    const match = heightInches.match(/(\d+)'(\d+)/);
    if (match) {
      const feet = parseInt(match[1]);
      const inches = parseInt(match[2]);
      const totalInches = feet * 12 + inches;
      return Math.round(totalInches * 2.54);
    }
    return 165; // default
  }

  // Format activity level for display
  private formatActivityLevel(level: string): string {
    switch (level.toLowerCase()) {
      case "sedentary":
        return "Sedentary";
      case "lightly_active":
        return "Lightly active";
      case "moderately_active":
        return "Moderately active";
      case "very_active":
        return "Very active";
      default:
        return level.charAt(0).toUpperCase() + level.slice(1);
    }
  }

  // Format diet type for display
  private formatDietType(diet: string): string {
    switch (diet.toLowerCase()) {
      case "classic":
        return "Classic";
      case "vegetarian":
        return "Vegetarian";
      case "vegan":
        return "Vegan";
      case "pescatarian":
        return "Pescatarian";
      case "low_carb":
        return "Low Carb / Keto";
      default:
        return diet.charAt(0).toUpperCase() + diet.slice(1);
    }
  }

  // Format fasting window for display
  private formatFastingWindow(window: string): string {
    switch (window.toLowerCase()) {
      case "sometimes_not_sure":
        return "Sometimes / Not sure";
      case "16:8":
        return "16:8 (Fast 16 hrs / Eat 8 hrs)";
      case "18:6":
        return "18:6 (Fast 18 hrs / Eat 6 hrs)";
      case "14:10":
        return "14:10 (Fast 14 hrs / Eat 10 hrs)";
      case "varies":
        return "My eating window varies";
      default:
        return window;
    }
  }

  // Get fasting preference text
  private getFastingPreference(window: string): string {
    const windowLower = window.toLowerCase();

    // If no fasting window or "no" preference
    if (!window || windowLower === "no" || windowLower === "none") {
      return "No — I don't follow a fasting window";
    }

    // If sometimes/not sure
    if (windowLower === "sometimes_not_sure" || windowLower === "sometimes") {
      return "Sometimes, not sure";
    }

    // If any specific fasting window (16:8, 18:6, etc.)
    return "Yes — I follow a fasting window";
  }

  // Process API response to match app display format
  private async processProfileData(
    apiData: ProfileApiResponse
  ): Promise<ProcessedProfileData> {
    // Get user preferences for units
    const preferences = await PreferencesService.getUserPreferences();
    // Calculate all possible values
    const weightKg = apiData.weight_kg;
    const weightLbs = apiData.weight_lbs ?? this.kgToLbs(apiData.weight_kg);
    const targetWeightKg = apiData.target_weight_kg;
    const targetWeightLbs =
      apiData.target_weight_lbs ?? this.kgToLbs(apiData.target_weight_kg);

    // Handle height - convert cm to inches if height_inches is null
    const heightCm = apiData.height_cm ?? 165; // default 165cm
    const heightInches = apiData.height_inches ?? this.cmToFeetInches(heightCm);

    return {
      // Basic Profile
      name: apiData.name,
      email: apiData.email,
      age: apiData.age,
      gender: apiData.gender.charAt(0).toUpperCase() + apiData.gender.slice(1),
      photo: apiData.photo,
      date_of_birth: apiData.date_of_birth ?? null,
      blood_type: apiData.blood_type ?? null,
      race: apiData.race ?? null,

      // Physical Information based on preferences
      weight: preferences.weight_unit === "kg" ? weightKg : weightLbs,
      weightUnit: preferences.weight_unit,
      weightKg: weightKg,
      weightLbs: weightLbs,

      height: preferences.height_unit === "cm" ? `${heightCm}cm` : heightInches,
      heightUnit: preferences.height_unit,
      heightInches: heightInches,
      heightCm: heightCm,
      height_ft: apiData.height_ft ?? null,
      height_inches: apiData.height_inches
        ? parseInt(apiData.height_inches)
        : null,

      // Target Information based on preferences
      targetWeight:
        preferences.weight_unit === "kg" ? targetWeightKg : targetWeightLbs,
      targetWeightKg: targetWeightKg,
      targetWeightLbs: targetWeightLbs,

      // Goals & Preferences with formatting
      weightLossGoal:
        apiData.goals.weight_loss_goal === "yes"
          ? "Yes"
          : apiData.goals.weight_loss_goal === "no"
            ? "No"
            : "Not Sure",
      activityLevel: this.formatActivityLevel(apiData.goals.activity_level),
      dietType: this.formatDietType(apiData.goals.diet_type),
      fastingWindow: this.formatFastingWindow(apiData.goals.fasting_window),
      fastingPreference: this.getFastingPreference(
        apiData.goals.fasting_window // Use raw API value, not formatted
      ),
    };
  }

  // Get user profile from API
  async getUserProfile(): Promise<ProcessedProfileData> {
    try {
      const response = await get<ProfileApiResponse>("/food_scanner/profile/");

      if (response?.success) {
        const processedData = await this.processProfileData(response);

        // Store in localStorage for offline access
        localStorage.setItem("userProfile", JSON.stringify(processedData));

        return processedData;
      } else {
        throw new Error("Failed to fetch profile data");
      }
    } catch (_error) {
      // Fallback to localStorage if API fails
      const cachedProfile = localStorage.getItem("userProfile");
      if (cachedProfile) {
        return JSON.parse(cachedProfile);
      }

      // Return default profile if no cache available
      return this.getDefaultProfile();
    }
  }

  // Update user profile (if API supports it)
  async updateUserProfile(
    profileData: Partial<ProcessedProfileData>
  ): Promise<boolean> {
    try {
      // This would need the update API endpoint when available
      // For now, just update localStorage
      const currentProfile = JSON.parse(
        localStorage.getItem("userProfile") ?? "{}"
      );
      const updatedProfile = { ...currentProfile, ...profileData };
      localStorage.setItem("userProfile", JSON.stringify(updatedProfile));

      return true;
    } catch (_error) {
      return false;
    }
  }

  // Get default profile data
  private getDefaultProfile(): ProcessedProfileData {
    return {
      name: "User",
      email: "user@example.com",
      age: 25,
      gender: "Female",
      photo: null,
      date_of_birth: null,
      blood_type: null,
      race: null,
      weight: 25.0, // Default to kg
      weightUnit: "kg",
      weightKg: 25.0,
      weightLbs: 55.1,
      height: "165cm", // Default to cm
      heightUnit: "cm",
      heightInches: "5'5",
      heightCm: 165,
      height_ft: 5,
      height_inches: 5,
      targetWeight: 20.4, // Default to kg
      targetWeightKg: 20.4,
      targetWeightLbs: 45.0,
      weightLossGoal: "Yes",
      activityLevel: "Sedentary",
      dietType: "Classic",
      fastingWindow: "Sometimes / Not sure",
      fastingPreference: "Sometimes, not sure",
    };
  }

  // Get edit profile data
  async getEditProfileData(): Promise<any> {
    const response = await get<any>("/food_scanner/edit-profile/");
    return response;
  }

  // Update profile data
  async updateProfile(profileData: any): Promise<any> {
    const response = await put<any>("/food_scanner/edit-profile/", {
      profile: profileData,
    });

    return response;
  }

  // Upload profile image (if API supports it)
  async uploadProfileImage(imageFile: File): Promise<string | null> {
    try {
      // This would need the image upload API endpoint when available
      // For now, convert to base64 and store locally
      return new Promise(resolve => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          localStorage.setItem("profileImage", result);
          resolve(result);
        };
        reader.readAsDataURL(imageFile);
      });
    } catch (_error) {
      return null;
    }
  }
}

export default ProfileService.getInstance();
